import Vue from 'vue';
import PaymentOptionStatusUpdate from '@/plugins/graphql/mutations/v2/payment-option-status-update.gql';
import PaymentOptionCreate from '@/plugins/graphql/mutations/v2/payment-option-create.gql';
import ShippingMethods from '@/plugins/graphql/queries/v2/shipping-methods.gql';
import MetaActivated from '@/plugins/graphql/queries/v3/meta-activated.gql';
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import PaymentOptionFormMixin from '@/helpers/vue_mixins/v2/PaymentOptionFormMixin';
import PaymentOptionForm from '@/components/v2/PaymentOptionForm.vue';
import ActionButton from '@/components/action_button/Index.vue';
import { mapGetters, mapMutations } from 'vuex';
import { DIY } from '@/helpers/constants/Store';
import '@/assets/stylesheets/v2/_option-card.scss';
import '@/assets/stylesheets/utils/helpers/_cursor.scss';
import DiyPreviewDrawerMixin from '@/helpers/vue_mixins/v2/DiyPreviewDrawerMixin';
import ThirdPartyPaymentsApplicationDialog from '@/components/v2/ThirdPartyPaymentsApplicationDialog.vue';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
export default Vue.extend({
    mixins: [
        AlertMixin,
        ViewportSizeMixin,
        DiyPreviewDrawerMixin,
        PaymentOptionFormMixin,
        CleverTapEventsMixin,
    ],
    props: {
        description: {
            type: String,
            default: null,
        },
        diy: {
            type: Boolean,
            default: false,
        },
        items: {
            type: Array,
            default: () => [],
        },
        thirdParty: {
            type: Boolean,
            default: false,
        },
        gateways: {
            type: Array,
            default: () => [],
        },
        refetchPaymentGateways: {
            type: Function,
            default: null,
        },
        kycStatus: {
            type: Object,
            default: null,
        },
        checkPartnerAccount: {
            type: Boolean,
            default: false,
        },
        mayaAvailabilityDate: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            lastActiveGateway: null,
            toggleForm: false,
            selectedPaymentGateWay: null,
            toggleThirdPartyDialog: false,
            switchModels: {},
            activeSwitch: 0,
            disabledSwitch: false,
        };
    },
    computed: {
        ...mapGetters({
            requestCurrentlyOnFlight: 'requestOnFlight',
        }),
        activeGateways() {
            return this.gateways.filter((gateway) => gateway.optionIsActive);
        },
        isLastActive() {
            return this.activeGateways.length === 1;
        },
    },
    watch: {
        items: {
            handler(val) {
                const self = this;
                // Generate Dynamic Switch Models based on payment gateway UUID
                self.updatePaymentGateways(self.items);
                if (val) {
                    val.forEach((item) => {
                        Vue.set(self.switchModels, item.baseGlobalId, item.optionIsActive);
                    });
                }
            },
            deep: true,
        },
        activeGateways: {
            handler(val) {
                this.updateLastActiveStatus(val);
            },
            immediate: true,
        },
    },
    methods: {
        ...mapMutations(['requestOnFlight', 'requestDone']),
        ...mapMutations(DIY, ['updatePaymentGateways']),
        openDialog(gateway) {
            const self = this;
            self.toggleForm = true;
            self.selectedPaymentGateWay = gateway;
        },
        openThirdPartyDialog() {
            const self = this;
            self.toggleThirdPartyDialog = !self.toggleThirdPartyDialog;
        },
        closeDialog() {
            this.$data.toggleForm = false;
            this.$data.selectedPaymentGateWay = null;
        },
        toggleSwitch(gateway) {
            gateway.optionIsActive = !gateway.optionIsActive;
            this.updateActiveGateways();
        },
        updateActiveGateways() {
            if (this.paymentGateways) {
                this.activeGateways = this.paymentGateways.filter(function (gateway) {
                    return gateway.optionIsActive;
                });
            }
        },
        updateLastActiveStatus(activeGateways) {
            const lastActiveGateway = activeGateways[activeGateways.length - 1]; // Get the last active gateway instead of the first one
            this.gateways.forEach((gateway) => {
                this.$set(gateway, 'disabled', false);
            });
            if (lastActiveGateway) {
                this.$set(lastActiveGateway, 'disabled', true);
            }
            this.activeSwitch = activeGateways.length;
            this.disabledSwitch = this.activeSwitch === 1;
        },
        async onStatusUpdate(gatewayName, optionUuid, active, gateway) {
            const self = this;
            self.requestOnFlight();
            const mutation = () => {
                if (active) {
                    // initial activation of COD by Ninja Van
                    self.paymentManagerAction('SUBMIT CREATE', gatewayName);
                    if (gatewayName === 'COD by Ninja Van' && !optionUuid) {
                        return self.createOption(gateway);
                    }
                    if (gatewayName === 'Xendit Payment Gateway' && !optionUuid) {
                        return self.createOption(gateway);
                    }
                    return self.enableOption(gatewayName, optionUuid, active);
                }
                return self.disableOption(gatewayName, optionUuid, active);
            };
            await mutation()
                .catch((e) => {
                self.errorNotification(e.message);
            })
                .finally(() => {
                self.requestDone();
            });
        },
        async enableOption(gatewayName, optionUuid, active) {
            return this.sendMutation(optionUuid, active).then(() => {
                this.successNotification(`${gatewayName} Successfully Enabled`);
            });
        },
        async disableOption(gatewayName, optionUuid, active) {
            return this.sendMutation(optionUuid, active).then(() => {
                this.successNotification(`${gatewayName} Successfully Disabled`);
            });
        },
        async sendMutation(optionUuid, active) {
            return this.$apollo.mutate({
                mutation: PaymentOptionStatusUpdate,
                variables: {
                    input: {
                        optionUuid,
                        active,
                    },
                },
            });
        },
        async createOption(gateway) {
            const vm = this;
            const paymentGatewayUuid = gateway.uuid;
            return this.$apollo
                .mutate({
                mutation: PaymentOptionCreate,
                variables: {
                    input: {
                        appUuid: this.appUuid,
                        paymentGatewayUuid: gateway.uuid,
                        paymentName: gateway.title,
                    },
                },
                update(cache, { data }) {
                    vm.syncPaymentGatewayCache(cache, data.paymentOptionCreate.option, paymentGatewayUuid);
                },
            })
                .then(() => {
                this.successNotification(`${gateway.title} Successfully Enabled`);
            })
                .catch((e) => {
                this.errorNotification(e.message);
                this.logError(e.message);
                this.requestDone();
                this.$emit('cancel');
            });
        },
        async checkMetaActivated(appUUID) {
            const response = await this.$apollo.query({
                query: MetaActivated,
                variables: {
                    appUuid: appUUID,
                },
                fetchPolicy: 'network-only',
            });
            const isMetaActivated = response?.data?.fetchAppByUuid?.metaActivated;
            return isMetaActivated;
        },
        async shippingQuery(appUUID) {
            let hasNinjaVanProvider;
            let ninjaVanStatus;
            const query = () => this.$apollo.query({
                query: ShippingMethods,
                variables: {
                    appUuid: appUUID,
                },
                fetchPolicy: 'network-only',
            });
            await query().then((response) => {
                const data = response.data.fetchAllShippingMethod.edges;
                hasNinjaVanProvider = data.find((s) => s.node.thirdPartyProvider === 'ninja_van');
                ninjaVanStatus = hasNinjaVanProvider?.node?.published;
            });
            return !!hasNinjaVanProvider && ninjaVanStatus;
        },
        async xenditQuery(appUUID) {
            let hasNinjaVanProvider;
            let ninjaVanStatus;
            const query = () => this.$apollo.query({
                query: ShippingMethods,
                variables: {
                    appUuid: appUUID,
                },
                fetchPolicy: 'network-only',
            });
            await query().then((response) => {
                const data = response.data.fetchAllShippingMethod.edges;
                hasNinjaVanProvider = data.find((s) => s.node.thirdPartyProvider === 'ninja_van');
                ninjaVanStatus = hasNinjaVanProvider?.node?.published;
            });
            return !!hasNinjaVanProvider && ninjaVanStatus;
        },
        async shippingRequiredDialog(appUUID, gateway, option, val) {
            const activatedShipping = await this.shippingQuery(appUUID);
            if (activatedShipping) {
                this.onStatusUpdate(gateway.title, option?.uuid, val, gateway);
                this.toggleSwitch(gateway);
            }
            else {
                this.openDialog(gateway);
            }
        },
        async xenditRequiredDialog(appUUID, gateway, option, val) {
            // const activatedShipping = await this.shippingQuery(appUUID);
            if (option) {
                this.onStatusUpdate(gateway.title, option?.uuid, val, gateway);
                this.toggleSwitch(gateway);
            }
            else {
                this.openDialog(gateway);
            }
            // this.createOption(gateway);
        },
        generateSwitch(gateway) {
            const self = this;
            const isLastActive = this.activeGateways[0]?.baseGlobalId === gateway.baseGlobalId && this.isLastActive;
            return this.$createElement('v-flex', { class: { 'shrink ml-5': true, 'align-self-end py-2': this.xsOnly } }, [
                this.$createElement('v-switch', {
                    props: {
                        inputValue: self.switchModels[gateway.baseGlobalId],
                        color: 'orange darken-3',
                        hideDetails: this.xsOnly,
                        disabled: this.disabledSwitch && isLastActive,
                    },
                    on: {
                        change: async (val) => {
                            const option = gateway.paymentOption;
                            const paymentName = gateway?.title;
                            const appUUID = self.appUuid;
                            const isNinjaVan = gateway.title === 'COD by Ninja Van';
                            const isXendit = gateway.title === 'Xendit Online Payment Gateway';
                            if (isNinjaVan) {
                                if (val) {
                                    self.paymentManagerAction('START CREATE', paymentName);
                                    self.shippingRequiredDialog(appUUID, gateway, option, val);
                                }
                                else {
                                    this.onStatusUpdate(gateway.title, option?.uuid, val);
                                    this.toggleSwitch(gateway);
                                }
                            }
                            if (isXendit) {
                                const key = gateway.baseGlobalId;
                                const isMetaActivated = await this.checkMetaActivated(appUUID);
                                if (isMetaActivated && val) {
                                    self.errorNotification('We cannot enable this payment option because Payment on Messenger is enabled on your app. If you want to continue, kindly ask for assistance to disable the Payment on Messenger.');
                                    Vue.set(self.switchModels, key, null);
                                    Vue.nextTick(() => {
                                        Vue.set(self.switchModels, key, false);
                                    });
                                    return;
                                }
                                if (val && !option) {
                                    self.paymentManagerAction('START CREATE', paymentName);
                                    self.xenditRequiredDialog(appUUID, gateway, option, val);
                                }
                                else {
                                    this.onStatusUpdate(gateway.title, option?.uuid, val);
                                    this.toggleSwitch(gateway);
                                }
                            }
                            if (val && !option && !isNinjaVan && !isXendit) {
                                self.paymentManagerAction('START CREATE', paymentName);
                                this.openDialog(gateway);
                            }
                            if ((!!val || !val) && !!option && !isNinjaVan) {
                                this.onStatusUpdate(gateway.title, option.uuid, val);
                                this.toggleSwitch(gateway);
                            }
                            if (val && this.lastActiveGateway && this.lastActiveGateway !== gateway) {
                                this.lastActiveGateway.disabled = false;
                            }
                            this.lastActiveGateway = val ? gateway : null;
                        },
                    },
                }),
            ]);
        },
        generatePreviewButton() {
            const self = this;
            if (self.mdAndDown && self.diy) {
                return this.$createElement('v-flex', { class: 'xs6 text-xs-right' }, [
                    this.$createElement('v-btn', {
                        props: {
                            flat: true,
                        },
                        class: 'preview pa-0 mr-0',
                        on: { click: this.showMiniAppPreview },
                    }, ['Preview', this.$createElement('v-icon', 'navigate_next')]),
                ]);
            }
        },
        generateEditButton(gateway) {
            const self = this;
            const paymentName = gateway?.title;
            return this.$createElement('v-flex', { class: { 'align-self-end mb-4': this.xsOnly } }, [
                this.$createElement(ActionButton, {
                    props: {
                        edit: true,
                        xsBtn: this.xsOnly,
                    },
                    on: {
                        wasClicked: () => {
                            self.paymentManagerAction('START EDIT', paymentName);
                            self.openDialog(gateway);
                        },
                    },
                }, [this.$createElement('v-icon', { props: { left: true } }, 'edit'), 'Edit']),
            ]);
        },
        generateThirdPartyApplicationButton() {
            return this.$createElement('v-flex', {
                class: { 'align-self-end mb-4': this.xsOnly, 'text-xs-right': true },
            }, [
                this.$createElement(ActionButton, {
                    props: {
                        orangeTheme: true,
                        xsBtn: this.xsOnly,
                    },
                    on: {
                        wasClicked: () => this.openThirdPartyDialog(),
                    },
                }, ['Apply']),
            ]);
        },
        generateAvailableOnProButton() {
            return this.$createElement('v-flex', {
                class: { 'align-self-end mb-4': this.xsOnly, 'text-xs-right': true },
            }, [
                this.$createElement(ActionButton, {
                    props: {
                        orangeTheme: true,
                        outline: true,
                        listAction: true,
                        smBtn: true,
                        xsBtn: this.xsOnly,
                    },
                    on: {
                        wasClicked: () => this.$router.push({ name: 'enterprise-plans' }),
                    },
                }, ['Available on Starter Plan']),
            ]);
        },
        formatStatus(status) {
            switch (status) {
                case 'VERIFICATION_IN_PROGRESS':
                    return 'In Progress';
                case 'DECLINED':
                    return 'Declined';
                case 'RESUBMISSION_REQUIRED':
                    return 'Update Required';
                case 'PASSED':
                    return 'Passed';
                case 'LIVE':
                    return 'Live';
                case 'FAILED':
                    return 'Failed';
                default:
                    return 'Pending Submission';
            }
        },
        getColor(status) {
            switch (status) {
                case 'LIVE':
                case 'PASSED':
                    return '#169F41';
                case 'DECLINED':
                case 'FAILED':
                    return '#FF0000';
                default:
                    return '#64b0c3';
            }
        },
        generateTextCopyCapabilities(kycStatus) {
            if (kycStatus?.capabilities?.some((capability) => capability.status === 'VERIFICATION_IN_PROGRESS')) {
                return null;
            }
            if (kycStatus?.capabilities?.some((capability) => capability.status === 'RESUBMISSION_REQUIRED')) {
                return this.$createElement('span', 'To Update the documents, ');
            }
            if (kycStatus?.capabilities?.every((capability) => capability.status === 'LIVE')) {
                return null;
            }
            return this.$createElement('span', 'Want to enable Gcash and Card Payment? ');
        },
        generateActions(gateway) {
            const self = this;
            const switchInput = self.generateSwitch(gateway);
            // const ninjaVan = 'COD by Ninja Van';
            const payment_with_no_edit_button = ['GCash', 'BillEase', 'Maya', 'COD by Ninja Van'];
            if (gateway.paymentOption) {
                if (!gateway.optionIsEnable) {
                    return [
                        this.$createElement('v-flex', { class: 'grow' }),
                        self.generateAvailableOnProButton(),
                    ];
                }
            }
            if (gateway.paymentOption &&
                !gateway.thirdPartyProvider &&
                !payment_with_no_edit_button.includes(gateway.paymentOption.paymentName)) {
                return [self.generateEditButton(gateway), switchInput];
            }
            if (gateway.thirdPartyOnlineOption || gateway.creditCard) {
                // if (gateway.title === 'Maya') {
                //   return [
                //     this.$createElement('v-flex', { class: 'grow' }),
                //     self.generateThirdPartyApplicationButton(),
                //   ];
                // }
                return [this.$createElement('v-flex', { class: 'grow' }), switchInput];
            }
            if (gateway.thirdPartyProvider &&
                !payment_with_no_edit_button.includes(gateway.paymentOption.paymentName)) {
                return [self.generateEditButton(gateway)];
            }
            return [this.$createElement('v-flex', { class: 'grow' }), switchInput];
        },
        generateMobileResponsive(gateway) {
            const self = this;
            const title = gateway.title === 'Maya' ? 'Online Payment' : gateway.title;
            return [
                self.$createElement('v-flex', { class: 'xs7' }, [
                    self.$createElement('v-layout', { class: 'column' }, [
                        self.$createElement('v-flex', { class: 'header', style: 'width: 100%' }, title),
                        self.$createElement('v-flex', { class: 'description mr-0' }, gateway.description),
                    ]),
                ]),
                self.$createElement('v-flex', [
                    self.$createElement('v-layout', { class: 'column reverse' }, self.generateActions(gateway)),
                ]),
            ];
        },
        generatePaymentMethod(gateway) {
            const self = this;
            if (this.xsOnly) {
                return self.generateMobileResponsive(gateway);
            }
            let processingFee = '';
            let netAmount = '';
            if (gateway && gateway.paymentOption) {
                const { paymentName } = gateway.paymentOption;
                if (paymentName === 'GCash' ||
                    paymentName === 'Maya' ||
                    paymentName === 'COD by Ninja Van') {
                    processingFee = '₱25.00';
                    netAmount = '₱975.00';
                }
                else if (paymentName === 'BillEase') {
                    processingFee = '₱15.00';
                    netAmount = '₱985.00';
                }
            }
            let paymentOptions;
            if (gateway && gateway.paymentOption && gateway.paymentOption.paymentName === 'GCash') {
                paymentOptions = [
                    self.$createElement('v-list-tile-content', { class: 'header shrink orange--text' }, gateway.title),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', { class: 'description' }, gateway.description),
                        self.$createElement('v-list-tile-content', { class: 'description', style: 'display: flex; flex-direction: row' }, [
                            self.$createElement('b', 'GLife:'),
                            self.$createElement('span', { style: 'margin-left: 3px' }, '+ 3.5% Processing Fee'),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', { class: 'processing-fee', style: 'display: flex; flex-direction: row' }, [
                            self.$createElement('p', 'Payment Processing Fee:'),
                            self.$createElement('p', { style: 'color: #169F41; margin-left: 3px' }, `${processingFee}`),
                        ]),
                        self.$createElement('v-list-tile-content', { class: 'processing-fee', style: 'display: flex; flex-direction: row' }, [
                            self.$createElement('p', 'Payment Processing Fee:'),
                            self.$createElement('p', { style: 'color: #169F41; margin-left: 3px' }, '₱35.00'),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', { class: 'net-amount', style: 'display: flex; flex-direction: row' }, [
                            self.$createElement('p', 'Net Amount:'),
                            self.$createElement('p', { style: 'color: #FF9800; margin-left: 3px' }, `${netAmount}`),
                        ]),
                        self.$createElement('v-list-tile-content', { class: 'net-amount', style: 'display: flex; flex-direction: row' }, [
                            self.$createElement('p', 'Net Amount:'),
                            self.$createElement('p', { style: 'color: #FF9800; margin-left: 3px' }, '₱965.00'),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-action', { style: 'min-width: 174px' }, [
                        self.$createElement('v-layout', self.generateActions(gateway)),
                    ]),
                ];
            }
            else if (gateway &&
                gateway.paymentOption &&
                gateway.paymentOption.paymentName === 'BillEase') {
                paymentOptions = [
                    self.$createElement('v-list-tile-content', { class: 'header shrink orange--text' }, gateway.title),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', { class: 'description' }, gateway.description),
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', {
                            class: 'processing-fee',
                            style: 'display: flex; flex-direction: row; align-items: center',
                        }, [
                            self.$createElement('p', 'Payment Processing Fee:'),
                            self.$createElement('p', { style: 'color: #169F41; margin-left: 3px' }, `${processingFee}`),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', {
                            class: 'net-amount',
                            style: 'display: flex; flex-direction: row; align-items: center',
                        }, [
                            self.$createElement('p', 'Net Amount:'),
                            self.$createElement('p', { style: 'color: #ff9800; margin-left: 3px' }, `${netAmount}`),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-action', { style: 'min-width: 174px' }, [
                        self.$createElement('v-layout', self.generateActions(gateway)),
                    ]),
                ];
            }
            else if (gateway && gateway.paymentOption && gateway.paymentOption.paymentName === 'Maya') {
                paymentOptions = [
                    self.$createElement('v-list-tile-content', { class: 'header shrink orange--text' }, [
                        'Maya Online Payment Gateway',
                        self.mayaAvailabilityDate ? self.$createElement('span', { style: 'color: black' }, `Available until: ${self.mayaAvailabilityDate}`) : '',
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', { class: 'description', style: 'height: 100px' }, 'GCash, Maya Wallet, ShopeePay, Credit/Debit Card'),
                        self.$createElement('v-list-tile-content', { class: 'description', style: 'display: flex; flex-direction: row' }, gateway.description),
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', {
                            class: 'processing-fee',
                            style: 'display: flex; flex-direction: row; align-items: center',
                        }, [
                            self.$createElement('p', 'Payment Processing Fee:'),
                            self.$createElement('p', { style: 'color: #169F41; margin-left: 3px' }, `${processingFee}`),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', {
                            class: 'net-amount',
                            style: 'display: flex; flex-direction: row; align-items: center',
                        }, [
                            self.$createElement('p', 'Net Amount:'),
                            self.$createElement('p', { style: 'color: #ff9800; margin-left: 3px' }, `${netAmount}`),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-action', { style: 'min-width: 174px' }, [
                        self.$createElement('v-layout', self.generateActions(gateway)),
                    ]),
                ];
            }
            else if (gateway.title === 'Xendit Online Payment Gateway') {
                paymentOptions = [
                    self.$createElement('v-list-tile-content', { class: 'header shrink orange--text', style: 'padding-right: 10px' }, gateway.title),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', {
                            class: 'processing-fee',
                            style: 'display: inline;',
                        }, [
                            self.$createElement('span', 'Check Xendit Fees and Taxes Here: '),
                            self.$createElement('a', {
                                attrs: {
                                    href: 'https://www.xendit.co/en-ph/pricing/',
                                    target: '_blank',
                                },
                                class: 'font-weight-bold',
                            }, 'Xendit Pricing'),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.kycStatus?.kycStatus?.status === 'VERIFICATION_IN_PROGRESS' ||
                            self.kycStatus?.capabilities?.some((capability) => capability.status === 'VERIFICATION_IN_PROGRESS') ||
                            !self.checkPartnerAccount
                            ? null
                            : self.$createElement('v-list-tile-content', {
                                class: 'processing-fee',
                                style: 'display: inline',
                            }, [
                                self.kycStatus?.kycStatus?.status === 'RESUBMISSION_REQUIRED'
                                    ? self.$createElement('span', 'To Update the documents, ')
                                    : null,
                                self.kycStatus?.kycStatus?.status === 'FAILED'
                                    ? self.$createElement('span', 'Want to enable Gcash and Card Payment? ')
                                    : null,
                                !self.kycStatus?.kycStatus?.status
                                    ? null
                                    : self.generateTextCopyCapabilities(self.kycStatus),
                                !self.kycStatus?.kycStatus?.status
                                    ? self.$createElement('span', 'Want to enable Gcash and Card Payment? ')
                                    : null,
                                self.kycStatus?.capabilities?.every((capability) => capability.status === 'LIVE') && self.kycStatus?.kycStatus?.status === 'PASSED'
                                    ? null
                                    : this.$createElement('a', {
                                        class: 'primary--text font-weight-bold cursor-pointer; align-items: center; cursor: pointer',
                                        on: { click: () => this.$router.push('kyc-verification') },
                                    }, 'Click here'),
                            ]),
                    ]),
                    self.$createElement('div', { class: 'description', style: 'display: block; align-items: center' }, [
                        self.checkPartnerAccount
                            ? self.$createElement('div', {
                                style: 'display: block; font-size: 13px; flex-direction: row; align-items: center',
                            }, [
                                self.$createElement('span', 'KYC Verification:'),
                                self.$createElement('span', {
                                    style: `color: ${self.getColor(self.kycStatus?.kycStatus?.status)}; margin-left: 3px`,
                                }, `${self.formatStatus(self.kycStatus?.kycStatus?.status)}`),
                            ])
                            : null,
                        self.kycStatus?.capabilities?.length > 0 && self.checkPartnerAccount
                            ? self.$createElement('div', [
                                self.$createElement('div', { style: 'display: flex; font-size: 13px;' }, [
                                    self.$createElement('span', 'CARDS:'),
                                    self.$createElement('span', {
                                        style: `color: ${self.getColor(self.kycStatus?.capabilities[0]?.status)}; margin-left: 3px`,
                                    }, `${self.formatStatus(self.kycStatus?.capabilities[0]?.status)}`),
                                ]),
                                self.$createElement('div', {
                                    style: 'display: flex; font-size: 13px;',
                                }, [
                                    self.$createElement('span', 'GCASH:'),
                                    self.$createElement('span', {
                                        style: `color: ${self.getColor(self.kycStatus?.capabilities[1]?.status)}; margin-left: 3px`,
                                    }, `${self.formatStatus(self.kycStatus?.capabilities[1]?.status)}`),
                                ]),
                            ])
                            : null,
                    ]),
                    self.$createElement('v-list-tile-action', { style: 'min-width: 174px' }, [
                        self.$createElement('v-layout', self.generateActions(gateway)),
                    ]),
                ];
            }
            else if (gateway &&
                gateway.paymentOption &&
                gateway.paymentOption.paymentName === 'COD by Ninja Van') {
                paymentOptions = [
                    self.$createElement('v-list-tile-content', { class: 'header shrink orange--text' }, gateway.title),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', { class: 'description' }, gateway.description),
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', {
                            class: 'processing-fee',
                            style: 'display: flex; flex-direction: row; align-items: center',
                        }, [
                            self.$createElement('p', 'Payment Processing Fee:'),
                            self.$createElement('p', { style: 'color: #169F41; margin-left: 3px' }, `${processingFee}`),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-content', { class: 'description' }, [
                        self.$createElement('v-list-tile-content', {
                            class: 'net-amount',
                            style: 'display: flex; flex-direction: row; align-items: center',
                        }, [
                            self.$createElement('p', 'Net Amount:'),
                            self.$createElement('p', { style: 'color: #ff9800; margin-left: 3px' }, `${netAmount}`),
                        ]),
                    ]),
                    self.$createElement('v-list-tile-action', { style: 'min-width: 174px' }, [
                        self.$createElement('v-layout', self.generateActions(gateway)),
                    ]),
                ];
            }
            else {
                paymentOptions = [
                    self.$createElement('v-list-tile-content', { class: 'header shrink orange--text' }, gateway.title === 'Maya' ? 'Online Payment' : gateway.title),
                    self.$createElement('v-list-tile-content', { class: 'description' }, gateway.description),
                    self.$createElement('v-list-tile-content', { class: 'description' }),
                    self.$createElement('v-list-tile-content', { class: 'description' }),
                    self.$createElement('v-list-tile-action', { style: 'min-width: 174px' }, [
                        self.$createElement('v-layout', self.generateActions(gateway)),
                    ]),
                ];
            }
            return paymentOptions;
        },
        generateMerchantRegistrationButton() {
            if (!this.diy && this.thirdParty) {
                return this.$createElement('div', { class: 'font-weight-regular pt-4' }, [
                    this.$createElement('span', 'For disbursement, kindly fill-up this form: '),
                    this.$createElement('span', {
                        class: 'primary--text font-weight-bold cursor-pointer',
                        on: { click: () => this.$router.push('merchant') },
                    }, 'Registration Form'),
                ]);
            }
        },
    },
    render(createElement) {
        const self = this;
        return createElement('v-card', { class: { 'option-card': true, '-diy': self.diy, 'pa-4': this.xsOnly } }, [
            createElement('v-card-title', { class: 'label pa-0 font-weight-bold' }, [
                createElement('v-flex', { class: 'xs6' }, self.label),
                self.generatePreviewButton(),
            ]),
            createElement('v-card-text', { class: 'description pa-0 hidden-xs-only' }, self.description),
            createElement('v-card-text', {
                class: {
                    'description pa-0 hidden-xs-only': self.label === 'Integrated Payment Options',
                    hidden: self.label === 'Manual Payments',
                },
            }, [
                createElement('p', {}, 'For a '),
                createElement('b', { style: 'margin: 0 4px; font-weight: 500' }, 'sample transaction amount of P1000.00'),
                createElement('p', {}, ' below are sample payment fee and net proceed computation'),
            ]),
            createElement('v-list', {
                class: {
                    list: true,
                    'online-list': self.label === 'Integrated Payment Options',
                },
            }, self.items.map((item) => {
                return [
                    createElement('v-list-tile', { class: { 'tile my-3': true, '-mobile-list': this.xsOnly } }, self.generatePaymentMethod(item)),
                    createElement('v-divider'),
                ];
            })),
            createElement(PaymentOptionForm, {
                props: {
                    toggle: self.toggleForm,
                    paymentGateway: self.selectedPaymentGateWay,
                },
                on: {
                    cancel: (gateway) => {
                        const key = gateway.baseGlobalId;
                        // In Order to trigger reactivity in switch model properties
                        // we first set value to null and update to actual value once
                        // object is updated via nextTick()
                        Vue.set(self.switchModels, key, null);
                        Vue.nextTick(() => {
                            Vue.set(self.switchModels, key, gateway.optionIsActive);
                        });
                        self.closeDialog();
                    },
                    success: () => {
                        this.$emit('refetch-partner-account-checker');
                        self.refetchPaymentGateways();
                        self.closeDialog();
                    },
                    xenditSuccess: (gateway) => {
                        this.$emit('refetch-partner-account-checker');
                        self.createOption(gateway);
                        self.closeDialog();
                    },
                },
            }),
            createElement(ThirdPartyPaymentsApplicationDialog, {
                props: {
                    toggle: self.toggleThirdPartyDialog,
                },
            }),
            this.generateMerchantRegistrationButton(),
        ]);
    },
});
